const ADMIN_E2E_BYPASS_AUTH_ENV_VAR = "VITE_E2E_BYPASS_AUTH";
const ADMIN_E2E_BYPASS_AUTH_OVERRIDE_STORAGE_KEY = "HC_ADMIN_E2E_BYPASS_AUTH";
const E2E_PERMISSION_KEYS_STORAGE_KEY = "HC_ADMIN_E2E_PERMISSION_KEYS";
const E2E_MENU_VISIBILITY_STORAGE_KEY = "HC_ADMIN_E2E_MENU_VISIBILITY";
const ADMIN_E2E_BYPASS_AUTH_WINDOW_FLAG = "__HC_ADMIN_E2E_BYPASS_AUTH__";
export const ADMIN_E2E_BYPASS_AUTH_TOKEN = "hc-admin-e2e-bypass-token";

type AdminE2eBypassWindow = Window &
  typeof globalThis & {
    [ADMIN_E2E_BYPASS_AUTH_WINDOW_FLAG]?: boolean;
  };

const normalizeEnvValue = (value: string | undefined): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : undefined;
};

const isTruthyEnvValue = (value: string | undefined): boolean => {
  const normalized = normalizeEnvValue(value)?.toLowerCase();
  if (!normalized) {
    return false;
  }

  return normalized === "true" || normalized === "1";
};

const isLocalhostOrigin = (hostnameOverride?: string): boolean => {
  const hostname =
    typeof hostnameOverride === "string"
      ? hostnameOverride.trim().toLowerCase()
      : typeof window !== "undefined"
        ? window.location.hostname.trim().toLowerCase()
        : "";
  return (
    hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1"
  );
};

/**
 * Admin Portal v2 foundations feature flag.
 *
 * @remarks
 * Defaults to enabled in all environments. The env var remains as an
 * emergency kill-switch (set `false`/`0`) when needed.
 */
export const isAdminV2FoundationsEnabled = (args?: {
  env?: ImportMetaEnv;
}): boolean => {
  void args;
  return true;
};

export const isAdminE2eBypassEnabled = (args?: {
  env?: ImportMetaEnv;
  hostnameOverride?: string;
}): boolean => {
  const env = args?.env ?? import.meta.env;
  const bypassEnabledFromEnv = isTruthyEnvValue(
    normalizeEnvValue(env[ADMIN_E2E_BYPASS_AUTH_ENV_VAR]),
  );

  if (typeof window === "undefined") {
    return bypassEnabledFromEnv;
  }

  const bypassWindow = window as AdminE2eBypassWindow;
  if (
    isLocalhostOrigin(args?.hostnameOverride) &&
    bypassWindow[ADMIN_E2E_BYPASS_AUTH_WINDOW_FLAG] === true
  ) {
    return true;
  }

  const overrideValue = normalizeEnvValue(
    window.localStorage.getItem(ADMIN_E2E_BYPASS_AUTH_OVERRIDE_STORAGE_KEY) ??
      undefined,
  )?.toLowerCase();

  if (
    !bypassEnabledFromEnv &&
    isLocalhostOrigin(args?.hostnameOverride) &&
    (overrideValue === "true" || overrideValue === "1")
  ) {
    // Local deterministic smoke runs can opt into the auth bypass even when
    // the dev server env was not propagated as expected. This remains
    // localhost-only and still requires an explicit storage flag.
    return true;
  }

  if (!bypassEnabledFromEnv) {
    return false;
  }

  if (overrideValue === "false" || overrideValue === "0") {
    return false;
  }

  return true;
};

const readJsonFromStorage = <T>(key: string): T | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const raw = window.localStorage.getItem(key);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

export const getE2eBypassPermissionKeys = (): string[] | null => {
  const parsed = readJsonFromStorage<unknown>(E2E_PERMISSION_KEYS_STORAGE_KEY);
  if (!Array.isArray(parsed)) {
    return null;
  }

  const keys = parsed.filter(
    (value) => typeof value === "string" && value.trim().length > 0,
  ) as string[];

  return keys.length > 0 ? keys : null;
};

export const getE2eBypassMenuVisibility = (): {
  revenue: boolean;
  learning: boolean;
  sales: boolean;
} | null => {
  const parsed = readJsonFromStorage<unknown>(E2E_MENU_VISIBILITY_STORAGE_KEY);
  if (!parsed || typeof parsed !== "object") {
    return null;
  }

  const value = parsed as {
    revenue?: unknown;
    learning?: unknown;
    sales?: unknown;
  };

  return {
    revenue: typeof value.revenue === "boolean" ? value.revenue : true,
    learning: typeof value.learning === "boolean" ? value.learning : true,
    sales: typeof value.sales === "boolean" ? value.sales : true,
  };
};
